import { Divider, Stack } from '@mui/material';
import useMoney from '../../../../../../hooks/useMoney';
import { useOrderValueVisibility } from '../../../../../../hooks/useOrderValueVisibility';
import LangEnum from '../../../../../../lang/LangEnum';
import useGeneralSettingsStore from '../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { joinNonEmpty } from '../../../../../../utils/formatters';
import MarkupChip from '../../../../../products/MarkupChip';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { Summary } from '../../../../products/orderProducts.types';
import OrderTotalsSummaryRow from './components/OrderTotalsSummaryRow';
import { ORDER_TOTALS_SUMMARY_GAP } from './orderTotalsSummary.consts';
import {
  TOTAL_NET_VALUE_ROW_KEY,
  buildOrderTotalsValueRows,
  calcTotalMarkup,
} from './orderTotalsSummary.utils';

interface OrderTotalsSummaryProps {
  totals: Summary;
  totalTestId?: string;
}

const OrderTotalsSummary = ({ totals, totalTestId }: OrderTotalsSummaryProps) => {
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();
  const { generalSettings } = useGeneralSettingsStore();
  const { shouldHideOrderValues } = useOrderValueVisibility();

  const valueRows = buildOrderTotalsValueRows({
    totals,
    showCharges: generalSettings.showPaymentMethodsAccumulatedInterest,
    showShippingCost: generalSettings.showOrderConditionShippingCost,
  });

  const formatAmount = (value: number) => formatMoney(Number.isFinite(value) ? value : 0);

  const totalRetail = totals.totalRetail ?? 0;
  const markup = calcTotalMarkup(totalRetail, totals.total);
  const showMarkup =
    generalSettings.enableMarkup && generalSettings.enableRetailPrice && markup > 0;

  return (
    <Stack sx={{ gap: ORDER_TOTALS_SUMMARY_GAP }}>
      <OrderTotalsSummaryRow
        label={translate(LangEnum.GLOBAL_ITEMS, { quantity: totals.quantity })}
        value={totals.quantity}
      />
      <OrderTotalsSummaryRow
        label={translate(LangEnum.GLOBAL_PIECES, { quantity: totals.pieces })}
        value={totals.pieces}
      />

      <Divider />

      {valueRows.map((row) => (
        <OrderTotalsSummaryRow
          key={row.key}
          label={row.labelText ?? joinNonEmpty([row.prefix, translate(row.label)], ' ')}
          value={joinNonEmpty([row.sign, formatAmount(row.value)], ' ')}
          changeKey={row.value}
          isNested={row.isNested}
          isLastNested={row.isLastNested}
          isEmphasized={row.isEmphasized}
          isValueHidden={shouldHideOrderValues}
          valueTestId={row.key === TOTAL_NET_VALUE_ROW_KEY ? totalTestId : undefined}
        />
      ))}

      {generalSettings.enableRetailPrice && (
        <>
          <Divider />
          <OrderTotalsSummaryRow
            label={translate(LangEnum.GLOBAL_SELLOUT_VALUE)}
            value={formatAmount(totalRetail)}
            changeKey={totalRetail}
            isValueHidden={shouldHideOrderValues}
          />
          {showMarkup && (
            <OrderTotalsSummaryRow
              label={translate(LangEnum.GLOBAL_MARKUP)}
              value={<MarkupChip markupValue={markup} />}
              changeKey={markup}
              isValueHidden={shouldHideOrderValues}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default OrderTotalsSummary;
